'use client';

import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { FaFacebookF, FaInstagram, FaPinterestP } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { TfiYoutube } from 'react-icons/tfi';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { CATEGORY_FOOTER_LINKS, EXTERNAL_LINKS, ROUTER_LINKS, SOCIAL_MEDIA_LINKS } from '@/shared/constants/links-constants';
import styles from './footer.module.scss';

// Every <Link> here sets prefetch={false}.
//
// The footer renders on every page, so when it scrolls into view the App Router
// fires an RSC request per link — each one a full server render of a static
// content page almost nobody clicks. Measured in the nginx access log on
// 2026-08-02: of 2000 requests over 9 seconds, 1351 (67.5%) were RSC prefetches
// and only 429 (21.4%) were real page loads. The top prefetch targets were
// exactly this list — how-roomster-works, trust-and-safety,
// safe-roommate-matching, community-standards, renting-out-a-room, press,
// finding-a-room, download-app, roommate-finder, privacy, terms, sitemaps —
// triggered from /profile/:id pages across a dozen locales.
//
// That is roughly 3 wasted server renders for every real page view, against a
// fleet whose ceiling is ~112 renders/sec. Prefetching a footer buys nothing:
// these are terminal destinations, not part of a browse flow.
export default function Footer() {
  const $ = _c(104);
  const linksTranslations = useTranslations("global.links");
  const loggedUser = getUserBearerCookie();
  const legalTranslations = useTranslations("global.legal");
  const t0 = cx(styles.footer, loggedUser && styles.footer__isUserLogged);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <a className={styles.footerLink} href={SOCIAL_MEDIA_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="FacebookIcon"><FaFacebookF /></a>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <a className={styles.footerLink} href={SOCIAL_MEDIA_LINKS.twitter} target="_blank" rel="noopener noreferrer" aria-label="TwitterIcon"><FaXTwitter /></a>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <a className={styles.footerLink} href={SOCIAL_MEDIA_LINKS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YoutubeIcon"><TfiYoutube /></a>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <a className={styles.footerLink} href={SOCIAL_MEDIA_LINKS.pinterest} target="_blank" rel="noopener noreferrer" aria-label="PinterestIcon"><FaPinterestP /></a>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={styles.mediaLinks}>{t1}{t2}{t3}{t4}<a className={styles.footerLink} href={SOCIAL_MEDIA_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="InstagramIcon"><FaInstagram /></a></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== linksTranslations || $[6] !== loggedUser) {
    t6 = !loggedUser && CATEGORY_FOOTER_LINKS.map(t7 => {
      const {
        listingType,
        translationKey
      } = t7;
      return <Link key={listingType} className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.listingTypeCategory(listingType)}>{linksTranslations(translationKey)}</Link>;
    });
    $[5] = linksTranslations;
    $[6] = loggedUser;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== linksTranslations || $[9] !== loggedUser) {
    t7 = !loggedUser && <Link prefetch={false} className={styles.footerLink} href={ROUTER_LINKS.partner}>{linksTranslations("partner")}</Link>;
    $[8] = linksTranslations;
    $[9] = loggedUser;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ROUTER_LINKS.sitemap("rooms-for-rent");
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== linksTranslations) {
    t9 = linksTranslations("sitemap");
    $[12] = linksTranslations;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <Link className={styles.footerLink} prefetch={false} href={t8}>{t9}</Link>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== linksTranslations) {
    t11 = linksTranslations("terms");
    $[16] = linksTranslations;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.terms}>{t11}</Link>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== linksTranslations) {
    t13 = linksTranslations("faq");
    $[20] = linksTranslations;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <a className={styles.footerLink} href={EXTERNAL_LINKS.faq} target="_blank" rel="noreferrer">{t13}</a>;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== linksTranslations) {
    t15 = linksTranslations("privacy");
    $[24] = linksTranslations;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.privacy}>{t15}</Link>;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== linksTranslations) {
    t17 = linksTranslations("press");
    $[28] = linksTranslations;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17) {
    t18 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.press}>{t17}</Link>;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== linksTranslations) {
    t19 = linksTranslations("affiliate-program");
    $[32] = linksTranslations;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19) {
    t20 = <a className={styles.footerLink} href={EXTERNAL_LINKS.affiliateProgram} target="_blank" rel="noreferrer">{t19}</a>;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== linksTranslations) {
    t21 = linksTranslations("fair-housing");
    $[36] = linksTranslations;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t21) {
    t22 = <a className={styles.footerLink} href={EXTERNAL_LINKS.fairHousing} target="_blank" rel="noreferrer">{t21}</a>;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== linksTranslations) {
    t23 = linksTranslations("blog");
    $[40] = linksTranslations;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t23) {
    t24 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.blog} target="_blank">{t23}</Link>;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== linksTranslations) {
    t25 = linksTranslations("download-app");
    $[44] = linksTranslations;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t25) {
    t26 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.downloadApp}>{t25}</Link>;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== linksTranslations) {
    t27 = linksTranslations("finding-a-room");
    $[48] = linksTranslations;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t27) {
    t28 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.findingRoom}>{t27}</Link>;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== linksTranslations) {
    t29 = linksTranslations("renting-out-a-room");
    $[52] = linksTranslations;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t29) {
    t30 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.rentingOutRoom}>{t29}</Link>;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== linksTranslations) {
    t31 = linksTranslations("roommate-finder");
    $[56] = linksTranslations;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== t31) {
    t32 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.roommateFinder}>{t31}</Link>;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== linksTranslations) {
    t33 = linksTranslations("trust-and-safety");
    $[60] = linksTranslations;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== t33) {
    t34 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.trustAndSafety}>{t33}</Link>;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== linksTranslations) {
    t35 = linksTranslations("community-standards");
    $[64] = linksTranslations;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t35) {
    t36 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.communityStandards}>{t35}</Link>;
    $[66] = t35;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] !== linksTranslations) {
    t37 = linksTranslations("safe-roommate-matching");
    $[68] = linksTranslations;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== t37) {
    t38 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.safeRoommateMatching}>{t37}</Link>;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== linksTranslations) {
    t39 = linksTranslations("how-roomster-works");
    $[72] = linksTranslations;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t39) {
    t40 = <Link className={styles.footerLink} prefetch={false} href={ROUTER_LINKS.howRoomsterWorks}>{t39}</Link>;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t10 || $[77] !== t12 || $[78] !== t14 || $[79] !== t16 || $[80] !== t18 || $[81] !== t20 || $[82] !== t22 || $[83] !== t24 || $[84] !== t26 || $[85] !== t28 || $[86] !== t30 || $[87] !== t32 || $[88] !== t34 || $[89] !== t36 || $[90] !== t38 || $[91] !== t40 || $[92] !== t6 || $[93] !== t7) {
    t41 = <div className={styles.links}>{t6}{t7}{t10}{t12}{t14}{t16}{t18}{t20}{t22}{t24}{t26}{t28}{t30}{t32}{t34}{t36}{t38}{t40}</div>;
    $[76] = t10;
    $[77] = t12;
    $[78] = t14;
    $[79] = t16;
    $[80] = t18;
    $[81] = t20;
    $[82] = t22;
    $[83] = t24;
    $[84] = t26;
    $[85] = t28;
    $[86] = t30;
    $[87] = t32;
    $[88] = t34;
    $[89] = t36;
    $[90] = t38;
    $[91] = t40;
    $[92] = t6;
    $[93] = t7;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] !== legalTranslations) {
    t42 = legalTranslations("do-not-share-personal-information");
    $[95] = legalTranslations;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  let t43;
  if ($[97] !== t42) {
    t43 = <div className={styles.links}><button className={styles.infoShare} onClick={_temp}>{t42}</button></div>;
    $[97] = t42;
    $[98] = t43;
  } else {
    t43 = $[98];
  }
  let t44;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className={styles.copyRight}>Roomster ©{" "}{new Date().getFullYear()}</div>;
    $[99] = t44;
  } else {
    t44 = $[99];
  }
  let t45;
  if ($[100] !== t0 || $[101] !== t41 || $[102] !== t43) {
    t45 = <div className={t0}>{t5}{t41}{t43}{t44}</div>;
    $[100] = t0;
    $[101] = t41;
    $[102] = t43;
    $[103] = t45;
  } else {
    t45 = $[103];
  }
  return t45;
}
function _temp(event) {
  event.preventDefault();
  if (globalThis.revisitCkyConsent) {
    globalThis.revisitCkyConsent();
  }
}